import type { ExperimentCell, ExperimentName } from 'shared/src/services/stats-ig.service';
import type { quizSteps } from '../data/quiz';

/**
 * A funnel's flow, screen by screen. Configs are plain data on purpose - conditions and A/B
 * patches are declarative, not callbacks - so a funnel can later be authored outside the code.
 * The format is documented in docs/funnel-flow-config.md.
 */

export type QuizStepKey = keyof typeof quizSteps;

// Every type but login and paywall lives at quiz/<screen index>; those two keep their own URLs,
// which emailGuard, checkout's back button and payment-result's retry link all point at.
export type ScreenType =
  | 'quiz'
  | 'intro'
  | 'customization'
  | 'formatInput'
  | 'ready'
  | 'login'
  | 'paywall';

/**
 * Holds when `step` has a stored answer and every option selected on it is one of `is`: one id for
 * a single-select step, and for a multi-select step "nothing but these" - `is: ['other']` on
 * `format` holds only when 'other' is the sole format picked.
 */
export interface AnswerCondition {
  step: QuizStepKey;
  is: readonly string[];
}

export interface ScreenConditions {
  showIf?: AnswerCondition;
  skipIf?: AnswerCondition;
}

export type FlowScreen = ScreenConditions &
  ({ type: 'quiz'; step: QuizStepKey } | { type: Exclude<ScreenType, 'quiz'> });

// A change to the flow: the quiz step `replace` gives way to `with`, at the same position.
export interface FlowPatch {
  replace: QuizStepKey;
  with: readonly FlowScreen[];
}

export interface FlowExperimentOf<N extends ExperimentName> {
  experiment: N;
  // The web_onboarding_started property that reports the visitor's cell.
  property: string;
  // When the exposure is logged. Without either, at bootstrap, so an exposure is exactly one
  // funnel visitor, bounces included. `exposedOn`: on the first render of one of those steps.
  // `exposedWhen`: once the visitor's answers meet the condition.
  exposedOn?: readonly QuizStepKey[];
  exposedWhen?: AnswerCondition;
  cells?: { [C in ExperimentCell<N>]?: readonly FlowPatch[] };
}

export type FlowExperiment = { [N in ExperimentName]: FlowExperimentOf<N> }[ExperimentName];

export interface FunnelFlowConfig {
  screens: readonly FlowScreen[];
  // Applied for visitors whose ad promoted a book (`?book=`).
  promotedBookPatches?: readonly FlowPatch[];
  experiments?: readonly FlowExperiment[];
}

export function quiz(step: QuizStepKey, conditions: ScreenConditions = {}): FlowScreen {
  return { type: 'quiz', step, ...conditions };
}

export function screen(
  type: Exclude<ScreenType, 'quiz'>,
  conditions: ScreenConditions = {}
): FlowScreen {
  return { type, ...conditions };
}
